import { FormEvent, useState } from 'react';
import { dealerErrorFields, dealerErrorMessage, dealersApi, isFeatureOff } from '../../services/dealersApi';
import { CreditPack, PackRequest, PackStatus } from '../../types/dealers';
import { describedBy, FormField, RadioGroup } from '../promotions/PromoFormParts';
import { discountError, sameAmount, wholeAedError } from './dealerFormat';

/** `DealerCreditSettings.MIN_PACK_FACE_VALUE_AED` / `MAX_FACE_VALUE_AED`: a pack is 1,000 to 1,000,000 whole AED (§3 Limits). */
const MIN_PACK_FACE = 1_000;
const MAX_FACE = 1_000_000;

interface PackFormProps {
  /** null = new pack. */
  pack: CreditPack | null;
  token: string;
  onClose: () => void;
  onSaved: (saved: CreditPack) => void;
  onFeatureOff: () => void;
}

/**
 * Spec 085 Flow C / AC-19 — a credit pack every ACTIVE dealer can buy. The dealer pays face × (1 −
 * the higher of the pack's and its own discount); the two never add up. INACTIVE hides it from new
 * orders; packs are never deleted.
 */
export default function PackForm({ pack, token, onClose, onSaved, onFeatureOff }: PackFormProps) {
  const editing = pack !== null;
  const [name, setName] = useState(pack?.name ?? '');
  const [face, setFace] = useState(pack ? String(Number(pack.faceValueAed)) : '');
  const [discount, setDiscount] = useState(pack ? String(Number(pack.discountPct)) : '');
  const [status, setStatus] = useState<PackStatus>(pack?.status ?? 'ACTIVE');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setFormError(null);
    const local: Record<string, string> = {};
    if (!name.trim()) local.name = 'Enter a name, e.g. "50k pack"';
    const f = wholeAedError(face, MIN_PACK_FACE, MAX_FACE);
    if (f) local.faceValueAed = f;
    const d = discountError(discount);
    if (d) local.discountPct = d;
    if (Object.keys(local).length) { setErrors(local); return; }
    setErrors({});

    let body: PackRequest = { name: name.trim(), faceValueAed: face.trim(), discountPct: discount.trim(), status };
    if (pack) {
      body = {
        ...(name.trim() !== pack.name ? { name: name.trim() } : {}),
        ...(!sameAmount(face, pack.faceValueAed) ? { faceValueAed: face.trim() } : {}),
        ...(!sameAmount(discount, pack.discountPct) ? { discountPct: discount.trim() } : {}),
        ...(status !== pack.status ? { status } : {}),
      };
      if (Object.keys(body).length === 0) { onClose(); return; }
    }
    setSaving(true);
    try {
      onSaved(pack ? await dealersApi.updatePack(pack.id, body, token) : await dealersApi.createPack(body, token));
    } catch (err) {
      if (isFeatureOff(err)) { onFeatureOff(); onClose(); return; }
      const fields = dealerErrorFields(err);
      if (Object.keys(fields).length) setErrors(fields);
      setFormError(dealerErrorMessage(err, 'Failed to save the pack'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal" role="dialog" aria-modal="true" aria-label={editing ? `Edit ${pack.name}` : 'New credit pack'} style={{ maxWidth: '520px', width: '100%' }}>
        <div className="admin-modal-header">
          <h3 style={{ margin: 0 }}>{editing ? `Edit ${pack.name}` : 'New credit pack'}</h3>
          <button type="button" className="admin-modal-close" onClick={onClose} disabled={saving} aria-label="Close">&times;</button>
        </div>
        <form onSubmit={submit} noValidate>
          <div className="admin-modal-body">
            {formError && <div className="admin-info-banner danger" role="alert" data-testid="pack-form-error">{formError}</div>}
            <FormField id="pack-name" label="Name" error={errors.name}>
              <input id="pack-name" className="admin-form-input" maxLength={100} value={name} disabled={saving}
                {...describedBy('pack-name', errors.name)} onChange={(e) => setName(e.target.value)} />
            </FormField>
            <FormField id="pack-face" label="Face value (AED)" error={errors.faceValueAed} hint="Whole AED, 1,000 to 1,000,000.">
              <input id="pack-face" className="admin-form-input" inputMode="numeric" value={face} disabled={saving}
                {...describedBy('pack-face', errors.faceValueAed, true)} onChange={(e) => setFace(e.target.value)} />
            </FormField>
            <FormField id="pack-discount" label="Discount (%)" error={errors.discountPct}
              hint="If the dealer's own discount is higher, the dealer gets that one instead.">
              <input id="pack-discount" className="admin-form-input" inputMode="decimal" value={discount} disabled={saving}
                {...describedBy('pack-discount', errors.discountPct, true)} onChange={(e) => setDiscount(e.target.value)} />
            </FormField>
            <RadioGroup name="pack-status" label="Status" value={status} disabled={saving} onChange={setStatus}
              options={[{ value: 'ACTIVE', label: 'Active (dealers can buy it)' }, { value: 'INACTIVE', label: 'Inactive' }]} />
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn admin-btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Create pack'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
